// @flow
import React from 'react';
import type { Node } from 'react';
import classNames from 'classnames';
import { FormattedText, Icon, Spinner } from '@elements';
import './FinancialItemCard.scss';

type Props = {
  title: Node,
  value: Node,
  details?: Node,
  // Left out for a summary row (the reports' Resumo): no date line
  date?: ?string,
  // What the date is, when it isn't plain ("Última Alteração")
  dateLabel?: string,
  // Left out for a single-doctor clinic, as the table hides its column
  doctorName?: ?string,
  status?: Node,
  isLoading?: boolean,
  onClick?: ?Function,
};

// A service, payment or receipt as the app lists it (the patient's financial lists and the reports):
// what it is and its value first, its details under them, then the date (and doctor) with the status.
// The whole card opens the item's menu, or the patient.
const FinancialItemCard = ({ title, value, details, date, dateLabel, doctorName, status, isLoading, onClick }: Props) => {
  const clickable = Boolean(onClick) && !isLoading;
  const handleKeyDown = (event: Object) => {
    if (!clickable || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    if (onClick) onClick();
  };

  return (
    <div
      className={classNames('financialItemCard', { clickable, loading: isLoading })}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable && onClick ? () => onClick() : undefined}
      onKeyDown={handleKeyDown}
    >
      <div className="financialItemCardBody">
        <div className="financialItemCardTop">
          <div className="financialItemCardTitle">{title}</div>
          <div className="financialItemCardValue">{value}</div>
        </div>
        <div className="financialItemCardDetails">{details}</div>
        {(date || doctorName || status) && (
          <div className="financialItemCardBottom">
            <div className="financialItemCardMeta">
              {date && (
                <React.Fragment>
                  {dateLabel && `${dateLabel} `}
                  <FormattedText type="date" text={date} />
                </React.Fragment>
              )}
              {doctorName && `${date ? ' · ' : ''}${doctorName}`}
            </div>
            {status && <div className="financialItemCardStatus">{status}</div>}
          </div>
        )}
      </div>
      <div className="financialItemCardAction">
        {isLoading && <Spinner />}
        {clickable && <Icon iconName="arrow-right" size={18} />}
      </div>
    </div>
  );
};

// The total under a list of FinancialItemCards (the reports)
export const FinancialItemTotal = ({ label, value }: { label: Node, value: Node }) => (
  <div className="financialItemTotal">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

export default FinancialItemCard;
